import { ComponentProps, useState } from "react";

import {
  CancellationFeedback,
  useCancelWeldSubscriptionMutation,
} from "@/apollo/types";
import {
  Button,
  PrimaryButton,
  SecondaryButton,
} from "@/components/elements/Button";
import { FormatTime } from "@/components/elements/FormatTime";
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
} from "@/components/elements/Modal";
import { TextArea } from "@/components/primitives/input/TextArea";
import { WELD_EXISTING_CUSTOMER_CALL_URL } from "@/constants/external-urls/hubspot";
import { useBillingDetails } from "@/features/billing/useBillingDetails";
import { useCurrentPlan } from "@/features/subscription";
import { useMixpanel } from "@/monitoring/mixpanel";
import { useToast } from "@/providers/ToastProvider";
import { RadioGroup } from "@headlessui/react";

import { useRevalidateSubscriptionCache } from "../create-subscription/hooks/useRevalidateSubscriptionCache";

const feedbackOptions = [
  {
    value: CancellationFeedback.TooExpensive,
    label: "It's too expensive",
  },
  {
    value: CancellationFeedback.MissingFeatures,
    label: "Lacking some essential features",
  },
  {
    value: CancellationFeedback.SwitchedService,
    label: "I found a better service",
  },
  {
    value: CancellationFeedback.Unused,
    label: "I don't use the service enough",
  },
  {
    value: CancellationFeedback.CustomerService,
    label: "Customer service was less than expected",
  },
  {
    value: CancellationFeedback.TooComplex,
    label: "Ease of use was less than expected",
  },
  {
    value: CancellationFeedback.LowQuality,
    label: "Too many technical problems",
  },
  {
    value: CancellationFeedback.Other,
    label: "Other reason",
  },
];

export function CancelSubscriptionDialog(props: ComponentProps<typeof Modal>) {
  const revalidateCache = useRevalidateSubscriptionCache();
  const toast = useToast();
  const mixpanel = useMixpanel();

  const [cancelSubscription, { called: isCancellingSubscription, reset }] =
    useCancelWeldSubscriptionMutation({
      onCompleted: async () => {
        await revalidateCache();
        toast(
          "Your subscription has been cancelled.",
          "You will be able to use Weld until the end of your current billing period.",
          "success",
        );
        props.onClose();
      },
      onError: (error) => {
        toast(
          "An error occurred while cancelling your subscription. Please try again later.",
          error.message,
          "error",
        );
        reset();
      },
    });
  const { data } = useBillingDetails();

  const [feedback, setFeedback] = useState<CancellationFeedback | null>(null);
  const [comment, setComment] = useState<string>("");

  const { subscription } = useCurrentPlan();

  if (!data) {
    return null;
  }

  return (
    <Modal {...props}>
      <ModalHeader>Cancel subcription</ModalHeader>
      <ModalCloseButton />
      <ModalBody className="flex flex-col gap-6">
        <div className="flex flex-col gap-2 text-sm">
          <p>
            If confirmed, your subscription will be cancelled on{" "}
            {subscription && (
              <FormatTime
                date={subscription.currentBillingPeriod.end}
                format="LL"
                className="font-semibold"
              />
            )}
            .
          </p>
          <p>
            We're sorry to see you go! In our continuous effort to enhance our
            services,{" "}
            <span className="font-semibold">
              could you please share more details about your decision to cancel?
            </span>{" "}
            Your valuable feedback will help us better understand your needs and
            improve our offerings for the future.
          </p>
        </div>
        <div className="">
          <RadioGroup value={feedback} onChange={setFeedback}>
            <div className="flex flex-col gap-2">
              {feedbackOptions.map((option) => (
                <RadioGroup.Option value={option.value} key={option.value}>
                  {({ checked }) => (
                    <div key={option.value} className="flex items-center gap-2">
                      <input
                        type="radio"
                        value={option.value}
                        checked={checked}
                        className="form-radio"
                        readOnly
                      />
                      <RadioGroup.Label>{option.label}</RadioGroup.Label>
                    </div>
                  )}
                </RadioGroup.Option>
              ))}
            </div>
          </RadioGroup>
        </div>
        <div>
          <TextArea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Please share any feedback you have"
          />
        </div>
      </ModalBody>
      <ModalFooter className="flex-row-reverse justify-start gap-4">
        <PrimaryButton
          colorScheme="danger"
          onClick={() => {
            if (
              subscription &&
              window.confirm(
                "Are you sure you want to cancel your subscription?",
              )
            ) {
              cancelSubscription({
                variables: {
                  input: {
                    subscriptionId: subscription.subscriptionId,
                    cancellationDetails: {
                      comment,
                      feedback,
                    },
                  },
                },
              });
              mixpanel.track("Cancel Subscription Requested", {
                feedback,
                comment,
              });
            }
          }}
          isDisabled={feedback == null}
          isLoading={isCancellingSubscription}
          loadingText="Cancelling ..."
        >
          Cancel subscription
        </PrimaryButton>
        <SecondaryButton onClick={() => props.onClose()}>
          Cancel
        </SecondaryButton>
      </ModalFooter>
    </Modal>
  );
}

export function CancelSubscriptionViaCallDialog(
  props: ComponentProps<typeof Modal>,
) {
  const mixpanel = useMixpanel();

  const currentPlan = useCurrentPlan();

  if (!currentPlan.subscription) {
    return null;
  }

  return (
    <Modal {...props}>
      <ModalHeader>Cancel subscription</ModalHeader>
      <ModalCloseButton />
      <ModalBody className="flex flex-col gap-6">
        <div className="flex flex-col gap-2 text-sm">
          <p>
            We're sorry to see you considering cancelling your subscription.
          </p>
          <p>
            Before you go, we'd love to talk with you to understand your needs
            and see how we can help. To cancel your subscription, please{" "}
            <span className="font-semibold">book a short meeting</span> with our
            team using the button below.
          </p>
        </div>
      </ModalBody>
      <ModalFooter className="flex-row-reverse justify-start gap-4">
        <Button
          as="a"
          variant="solid"
          colorScheme="primary"
          href={WELD_EXISTING_CUSTOMER_CALL_URL}
          target="_blank"
          rel="noreferrer"
          onClick={() => {
            mixpanel.track("Cancel Subscription Book Meeting Clicked");
          }}
        >
          Book a meeting
        </Button>
        <SecondaryButton onClick={() => props.onClose()}>Close</SecondaryButton>
      </ModalFooter>
    </Modal>
  );
}
